import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, Option, Select, Stack } from "@mui/joy";
import equal from "fast-deep-equal/es6";
import { ApiLine, ApiVenueMap, ClimbType } from "griptonite-api-client";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import { useEffect, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import { Route } from "@/core/types";
import {
  useCurrentVenueLineLookup,
  useCurrentVenueRouteLookup,
  useVenueCircuitsByUuid,
  useVenueLinesQuery,
  useVenueRoutesByClimbTypeQuery,
} from "@/hooks/routes";
import {
  useCurrentVenueMapsByUuidQuery,
  useCurrentVenueQuery,
  useCurrentVenueUuid,
} from "@/hooks/venues";
import { getPinGradeValue } from "@/utils/grades";

import Map from "../Map/Map";
import MapLinePinSvg from "../Map/MapLinePinSvg";
import { MapRoutePinSvgRaw } from "../Map/MapRoutePinSvg";
import { LineRoutes, MOUSE_OFFSET, RouteWithMap, SportMap } from "../Map/MapsList";
import Transform from "../Map/transform";

// type Props = {
//   prefix?: string;
// }

type Props = {
  mapPosition: { x: number; y: number } | null;
  map: string | null;
  setMapPosition: (mapPosition: { x: number; y: number }) => void;
  setMap: (map: string | null) => void;
  disabled?: boolean;
  filterPredicate?: (route: Route) => boolean;
  route: Route;
};

const emptyObject: Record<string, never> = {};
const emptyArray: never[] = [];
const emptyRouteUuids: Set<string> = new Set();

export const MapSwitcher = ({
  map,
  mapsByUuid,
  onChange,
  disabled,
}: {
  map: string | null;
  mapsByUuid?: Record<string, ApiVenueMap>;
  onChange: (map: string | null) => void;
  disabled?: boolean;
}) => {
  return (
    <Select
      size="sm"
      value={map}
      indicator={<KeyboardArrowDown sx={{ width: 16, height: 16 }} />}
      disabled={disabled}
      renderValue={(value) => {
        return value ? value.label : "Select map...";
      }}
      sx={{
        fontSize: "13px",
        paddingX: 0.75,
        minHeight: "24px",
        backgroundColor: "neutral.900",
        border: "1px solid",
        borderColor: "neutral.700",
        color: "neutral.300",
        "&:hover": {
          backgroundColor: "neutral.800",
        },
        ".MuiSelect-indicator": {
          marginLeft: 0.5,
        },
        ".MuiSelect-indicator .MuiSvgIcon-root": {
          color: "neutral.300",
        },
        "&.Mui-disabled": {
          borderColor: "transparent",
        },
        "&.Mui-disabled .MuiSelect-indicator": {
          display: "none",
        },
      }}
      onChange={(_e, value) => {
        onChange(value);
      }}
    >
      {mapsByUuid
        ? Object.values(mapsByUuid)
            ?.filter((x) => x.available)
            .map((map) => {
              return (
                <Option key={map.uuid} value={map.uuid}>
                  {map.name}
                </Option>
              );
            })
        : null}
    </Select>
  );
};

const MapEditor = ({
  mapPosition,
  map: mapUuid,
  route,
  setMapPosition,
  setMap,
  disabled,
  filterPredicate,
}: Props) => {
  const { data: venue } = useCurrentVenueQuery();
  const { data: routes } = useVenueRoutesByClimbTypeQuery(venue?.uuid, route.climbType);

  const defaultValues = useMemo(() => {
    const defaultMap = venue?.maps.find((m) => m.available);
    if (defaultMap) {
      return {
        map: defaultMap?.uuid,
        mapPosition: { x: defaultMap.width / 2, y: defaultMap.height / 2 },
      };
    }
    return null;
  }, [venue?.maps]);

  mapUuid = (mapUuid || defaultValues?.map) ?? null;
  mapPosition = (mapPosition || defaultValues?.mapPosition) ?? null;

  const { data: mapsByUuid } = useCurrentVenueMapsByUuidQuery();

  const editingRoute = route;

  const filteredRouteSet = useMemo(() => {
    if (!filterPredicate || !routes) {
      return null;
    }
    return new Set(routes.filter(filterPredicate).map((r) => r.uuid));
  }, [filterPredicate, routes]);

  const routesByMap = useMemo(() => {
    if (!routes) {
      return emptyObject as {
        ""?: Route[];
      } & Record<string, RouteWithMap[]>;
    }
    const result: { ""?: Route[] } & Record<string, RouteWithMap[]> = {};

    sortBy(routes, (x) => (filteredRouteSet?.has(x.uuid) ? 1 : 0)).forEach((r) => {
      if (r.uuid === editingRoute.uuid) {
        return;
      }
      const mapUuid = r.map && r.mapPosition ? r.map : "";
      if (!result[mapUuid]) {
        result[mapUuid] = [];
      }
      result[mapUuid].push(r as RouteWithMap);
    });

    const mapKey = mapUuid && mapPosition ? mapUuid : "";
    result[mapKey] = result[mapKey] || [];
    result[mapKey].push({
      ...route,
      map: mapUuid,
      mapPosition,
    } as RouteWithMap);
    return result;
  }, [routes, mapUuid, route, editingRoute.uuid, mapPosition, filteredRouteSet]);

  const [draggingPosition, setDraggingPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const draggingPositionRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    draggingPositionRef.current = draggingPosition;
  }, [draggingPosition]);

  const onDragStart = () => {
    if (!mapContainerRef.current || disabled) {
      return;
    }
    setDraggingPosition(mapPosition);
    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientX = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
      const clientY = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;

      const pinPos = {
        x: clientX - MOUSE_OFFSET.x,
        y: clientY - MOUSE_OFFSET.y,
      };
      const mapEl = mapContainerRef.current?.children[0] as HTMLDivElement | undefined;
      if (!mapEl) {
        throw new Error("Map container missing");
      }
      const rect = mapEl.getBoundingClientRect();
      const mapScreenCoords = [pinPos.x - rect.x, pinPos.y - rect.y];
      const mapTransform = mapEl.dataset.transform
        ? new Transform(...(JSON.parse(mapEl.dataset.transform) as [number, number, number]))
        : undefined;
      if (!mapTransform) {
        throw new Error("Map transform missing");
      }
      const mapPosition = {
        x: mapTransform.invertX(mapScreenCoords[0]),
        y: mapTransform.invertY(mapScreenCoords[1]),
      };
      console.log({
        ...mapPosition,
        clientX,
        clientY,
        pinPos,
      });
      setDraggingPosition(mapPosition);
    };
    const onEnd = () => {
      setMapPosition(draggingPositionRef.current!);
      setDraggingPosition(null);
      if (!mapContainerRef.current) {
        return;
      }
      mapContainerRef.current.removeEventListener("mousemove", onMove);
      mapContainerRef.current.removeEventListener("touchmove", onMove);
      mapContainerRef.current.removeEventListener("mouseup", onEnd);
      mapContainerRef.current.removeEventListener("touchend", onEnd);
    };
    mapContainerRef.current.addEventListener("mousemove", onMove);
    mapContainerRef.current.addEventListener("touchmove", onMove);
    mapContainerRef.current.addEventListener("mouseup", onEnd);
    mapContainerRef.current.addEventListener("touchend", onEnd);
  };

  return (
    <Stack sx={{ backgroundColor: "neutral.900", position: "relative" }}>
      <Box
        sx={{ height: "390px" }}
        className="map-container"
        ref={mapContainerRef}
        onClick={(e) => {
          if (disabled) {
            return;
          }
          // Get the click location relative to the map container
          const mapEl = mapContainerRef.current?.children[0] as HTMLDivElement | undefined;
          if (!mapEl) {
            return;
          }
          const rect = mapEl.getBoundingClientRect();
          const mapScreenCoords = [e.clientX - rect.x, e.clientY - rect.y];
          const mapTransform = mapEl.dataset.transform
            ? new Transform(...(JSON.parse(mapEl.dataset.transform) as [number, number, number]))
            : undefined;
          if (!mapTransform) {
            return;
          }
          const mapPosition = {
            x: mapTransform.invertX(mapScreenCoords[0]),
            y: mapTransform.invertY(mapScreenCoords[1]),
          };
          setDraggingPosition(mapPosition);
        }}
      >
        {mapUuid && mapsByUuid ? (
          <Map
            map={mapsByUuid[mapUuid]}
            mapObjects={routesByMap[mapUuid] || emptyArray}
            bboxPadding={[10, 10, 30, 10]}
            maxZoom={10}
            idAccessor={(route) => route.uuid}
            zoomToBbox={false}
            selectedIds={new Set()}
            onSetSelectedIds={() => null}
            brushEnabled={false}
            renderMapObject={(route, transform, _isSelected) => {
              const isFilteredOut = filteredRouteSet ? !filteredRouteSet?.has(route.uuid) : false;
              let scale = route.uuid === editingRoute.uuid ? 1.5 : 0.5;
              if (isFilteredOut && route.uuid !== editingRoute.uuid) {
                scale = 0.25;
              }
              const position =
                route.uuid === editingRoute.uuid && draggingPosition
                  ? draggingPosition
                  : route.mapPosition;
              return (
                <g transform={`translate(${position.x}, ${position.y})`} key={route.uuid}>
                  <g transform={`scale(${scale / Math.min(transform.k, 4)})`} opacity={1}>
                    <MapRoutePinSvgRaw
                      route={route}
                      dragToMoveEnabled={!disabled}
                      disableTagAlert
                      onDragStart={onDragStart}
                    />
                  </g>
                </g>
              );
            }}
          />
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          height: 0,
          position: "absolute",
          right: 8,
          bottom: 48,
        }}
      >
        <MapSwitcher
          map={mapUuid}
          onChange={(mapUuid) => {
            setMap(mapUuid);
            if (!mapUuid) {
              return;
            }
            const map = mapsByUuid?.[mapUuid];
            if (map) {
              setMapPosition({
                x: map.width / 2,
                y: map.height / 2,
              });
            }
          }}
          mapsByUuid={mapsByUuid}
        />
      </Box>
    </Stack>
  );
};

type InputProps = {
  prefix?: string;
  route: Route;
  disabled: boolean;
  filterPredicate?: (route: Route) => boolean;
};

export const MapPositionInput = ({ prefix, route, disabled, filterPredicate }: InputProps) => {
  const applyPrefix = (p: string | undefined, s: string) => (p ? `${p}.${s}` : s);

  const { control } = useFormContext();
  const { field: mapPositionField } = useController({
    control,
    name: applyPrefix(prefix, "mapPosition"),
  });
  const { field: mapField } = useController({
    control,
    name: applyPrefix(prefix, "map"),
  });

  const currentVenueUuid = useCurrentVenueUuid();
  const { data: venue } = useCurrentVenueQuery();
  const circuits = useVenueCircuitsByUuid(currentVenueUuid);

  const [mergedRoute, setMergedRoute] = useState(route);
  const { watch } = useFormContext();
  const prevValuesRef = useRef<unknown>(null);

  useEffect(() => {
    const subscription = watch((values) => {
      if (equal(values, prevValuesRef.current)) {
        return;
      }
      const circuit = circuits?.[values.route.circuit?.uuid];
      setMergedRoute({
        ...route,
        name: values.route.name,
        colors: values.route.colors,
        gradeEstimated: values.route.gradeEstimated,
        gradeValue: values.route.gradeValue,
        gradeSystem: values.route.gradeSystem,
        circuitNumber: values.route.circuit?.number ?? null,
        circuit: circuit
          ? {
              uuid: circuit.uuid,
              name: circuit.name,
              number: values.route.circuit?.number ?? null,
              difficulty: values.route.circuit?.difficulty,
              routeDisplay: circuit.routeDisplay || "NUMBER",
            }
          : route.circuit,
        pinGradeValue: getPinGradeValue(values.route.gradeValue, values.route.gradeSystem),
      });
      console.log({
        ...route,
        name: values.route.name,
        colors: values.route.colors,
        gradeEstimated: values.route.gradeEstimated,
        gradeValue: values.route.gradeValue,
        gradeSystem: values.route.gradeSystem,
        circuitNumber: values.route.circuit?.number ?? null,
        circuit: circuit
          ? {
              uuid: circuit.uuid,
              name: circuit.name,
              number: values.route.circuit?.number ?? null,
              difficulty: values.route.circuit?.difficulty,
              routeDisplay: circuit.routeDisplay || "NUMBER",
            }
          : route.circuit,
        pinGradeValue: getPinGradeValue(values.route.gradeValue, values.route.gradeSystem),
      });
      // Need to copy the values object here because otherwise fast-deep-equal short-circuits as it
      // sees the same object reference.
      prevValuesRef.current = structuredClone(values);
    }) as { unsubscribe: () => void };
    return () => subscription.unsubscribe();
  }, [circuits, route, watch]);

  console.log({ mergedRoute });

  return (
    <MapEditor
      mapPosition={mapPositionField.value}
      map={mapField.value}
      route={mergedRoute}
      setMapPosition={(value) => {
        mapPositionField.onChange(value);
        if (!mapField.value) {
          const defaultMapUuid = venue?.maps.find((m) => m.available)?.uuid;
          if (defaultMapUuid) {
            mapField.onChange(defaultMapUuid);
          }
        }
      }}
      setMap={(value) => mapField.onChange(value)}
      disabled={disabled}
      filterPredicate={filterPredicate}
    />
  );
};

export const LineSelectionMap = ({
  line,
  setLine,
}: {
  line: ApiLine | null;
  setLine: (line: ApiLine) => void;
}) => {
  const selectedLine = line;
  const [map, setMap] = useState<string | null>(line?.map || null);
  const { data: venue } = useCurrentVenueQuery();

  useEffect(() => {
    if (!map) {
      setMap(venue?.maps.find((m) => m.available)?.uuid || null);
    }
  }, [map, venue]);

  const { data: lines } = useVenueLinesQuery(venue?.uuid);
  const { data: mapsByUuid } = useCurrentVenueMapsByUuidQuery();

  const linesByMap = useMemo(() => {
    if (!lines) {
      return emptyObject as Record<string, ApiLine[]>;
    }
    const result: Record<string, ApiLine[]> = {};
    lines.forEach((line) => {
      if (!line.map || !line.mapPosition) {
        return;
      }
      result[line.map] = result[line.map] || [];
      result[line.map].push(line);
    });
    return result;
  }, [lines]);

  const selectedIds = useMemo(() => {
    return new Set(selectedLine?.uuid ? [selectedLine.uuid] : []);
  }, [selectedLine?.uuid]);

  return (
    <Stack sx={{ backgroundColor: "neutral.900", position: "relative" }}>
      <Box sx={{ height: "390px" }} className="map-container">
        {map && mapsByUuid && (
          <Map
            zoomToBbox={false}
            bboxPadding={10}
            maxZoom={10}
            idAccessor={(x) => x.uuid}
            mapObjects={linesByMap?.[map] || emptyArray}
            map={mapsByUuid[map]}
            selectedIds={selectedIds}
            onSetSelectedIds={(lineUuids) => {
              const lineUuid = lineUuids[0];
              const line = lines?.find((l) => l.uuid === lineUuid);
              if (line) {
                setLine(line);
              }
            }}
            brushEnabled={false}
            renderMapObject={(line, transform, isSelected) => {
              return (
                <g
                  key={line.uuid}
                  transform={`translate(${line.mapPosition.x}, ${line.mapPosition.y})`}
                  // opacity={isSelected ? 1 : 0.5}
                >
                  <g transform={`scale(${1 / Math.min(transform.k, 1.5)})`}>
                    <MapLinePinSvg
                      onClick={() => setLine(line)}
                      line={line}
                      isHighlighted={isSelected}
                    />
                  </g>
                </g>
              );
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          height: 0,
          position: "absolute",
          bottom: 48,
          left: 0,
          right: 4,
        }}
      >
        <MapSwitcher map={map} onChange={(value) => setMap(value)} mapsByUuid={mapsByUuid} />
      </Box>
    </Stack>
  );
};

/**
 * Narrows a venue's maps to the ones a route can actually be picked on, and
 * picks the busiest of them to open with.
 *
 * Derives from the same groupings the map renders from, so the switcher can't
 * offer a map that turns out to be empty.
 */
const useSelectableMaps = ({
  routesByMap,
  linesRoutesByMap,
  mapsByUuid,
  currentMapUuid,
}: {
  routesByMap: Record<string, RouteWithMap[]>;
  linesRoutesByMap: { ""?: Route[] } & Record<string, LineRoutes[]>;
  mapsByUuid?: Record<string, ApiVenueMap>;
  currentMapUuid?: string | null;
}) => {
  // A route counts towards a map if it has a pin of its own there, or if it
  // sits on a line placed on that map. Counting by uuid keeps routes that have
  // both from being counted twice.
  const routeCountByMap = useMemo(() => {
    const routeUuidsByMap: Record<string, Set<string>> = {};
    const add = (mapUuid: string, routeUuid?: string) => {
      if (!routeUuid) {
        return;
      }
      if (!routeUuidsByMap[mapUuid]) {
        routeUuidsByMap[mapUuid] = new Set();
      }
      routeUuidsByMap[mapUuid].add(routeUuid);
    };

    Object.entries(routesByMap).forEach(([mapUuid, mapRoutes]) => {
      mapRoutes.forEach((mapRoute) => add(mapUuid, mapRoute.uuid));
    });

    Object.entries(linesRoutesByMap).forEach(([mapUuid, mapLinesRoutes]) => {
      // The "" key holds routes with no line rather than a map's lines.
      if (!mapUuid) {
        return;
      }
      (mapLinesRoutes as LineRoutes[])?.forEach((lineRoutes) =>
        lineRoutes.routes.forEach((lineRoute) => add(mapUuid, lineRoute.uuid)),
      );
    });

    const result: Record<string, number> = {};
    Object.entries(routeUuidsByMap).forEach(([mapUuid, routeUuids]) => {
      result[mapUuid] = routeUuids.size;
    });
    return result;
  }, [routesByMap, linesRoutesByMap]);

  // Keep the current route's map whatever its count, so the switcher always has
  // an option matching its own value.
  const selectableMapsByUuid = useMemo(() => {
    if (!mapsByUuid) {
      return undefined;
    }
    const result: Record<string, ApiVenueMap> = {};
    Object.values(mapsByUuid).forEach((venueMap) => {
      if (routeCountByMap[venueMap.uuid] || venueMap.uuid === currentMapUuid) {
        result[venueMap.uuid] = venueMap;
      }
    });
    return result;
  }, [mapsByUuid, routeCountByMap, currentMapUuid]);

  const defaultMapUuid = useMemo(() => {
    const candidates = Object.values(selectableMapsByUuid ?? emptyObject).filter(
      (venueMap) => venueMap.available,
    );
    return (
      sortBy(candidates, (venueMap) => -(routeCountByMap[venueMap.uuid] ?? 0))[0]?.uuid ?? null
    );
  }, [selectableMapsByUuid, routeCountByMap]);

  return { selectableMapsByUuid, defaultMapUuid };
};

export const RouteSelectionMap = ({
  route,
  setRoute,
  climbType,
}: {
  route: Route | null;
  setRoute: (route: Route) => void;
  climbType: ClimbType;
}) => {
  const [map, setMap] = useState<string | null>(route?.map || null);
  const { data: venue } = useCurrentVenueQuery();

  const { data: lines } = useVenueLinesQuery(climbType === "SPORT" ? venue?.uuid : undefined);
  const { data: routes } = useVenueRoutesByClimbTypeQuery(venue?.uuid, climbType);
  const { data: mapsByUuid } = useCurrentVenueMapsByUuidQuery();
  const routesByMap = useMemo(() => {
    if (!routes) {
      return emptyObject as Record<string, RouteWithMap[]>;
    }
    const result: Record<string, RouteWithMap[]> = {};

    routes.forEach((route) => {
      if (!route.map || !route.mapPosition) {
        return;
      }
      const mapUuid = route.map;
      if (!result[mapUuid]) {
        result[mapUuid] = [];
      }
      result[mapUuid].push(route as RouteWithMap);
    });
    return result;
  }, [routes]);

  const linesRoutesByMap = useMemo(() => {
    if (!routes || !lines) {
      return emptyObject as { ""?: Route[] } & Record<string, LineRoutes[]>;
    }
    const result: { ""?: Route[] } & Record<string, LineRoutes[]> = {};
    const routesByLine = groupBy(routes, (route) => route.startLine?.uuid || "");
    result[""] = routesByLine[""];
    lines.forEach((line) => {
      if (!line.map || !line.mapPosition) {
        return;
      }
      result[line.map] = result[line.map] || [];
      result[line.map].push({
        line,
        routes: routesByLine[line.uuid] || [],
        mapPosition: line.mapPosition,
      });
    });
    return result;
  }, [routes, lines]);

  // Sport routes that aren't on a line still need pins of their own, or they
  // can't be picked.
  const nonLineRoutesByMap = useMemo(() => {
    const result: Record<string, RouteWithMap[]> = {};
    Object.entries(routesByMap).forEach(([mapUuid, mapRoutes]) => {
      result[mapUuid] = mapRoutes.filter((route) => !route.startLine);
    });
    return result;
  }, [routesByMap]);

  const { selectableMapsByUuid, defaultMapUuid } = useSelectableMaps({
    routesByMap,
    linesRoutesByMap,
    mapsByUuid,
    currentMapUuid: route?.map,
  });

  // Opens on the busiest map, and waits for the routes to load rather than
  // settling on one that turns out to have nothing on it.
  useEffect(() => {
    if (!map && defaultMapUuid) {
      setMap(defaultMapUuid);
    }
  }, [map, defaultMapUuid]);

  const [expandedLineUuid, setExpandedLineUuid] = useState<string | null>(null);
  useEffect(() => {
    if (route?.startLine) {
      setExpandedLineUuid(route.startLine.uuid);
    }
  }, [route]);

  const selectedUuidsSet = useMemo(() => new Set(route ? [route.uuid] : []), [route]);

  return (
    <Stack sx={{ backgroundColor: "neutral.900", position: "relative" }}>
      <Box
        sx={{ height: "390px", display: "flex", flexDirection: "column" }}
        className="map-container"
      >
        {map && mapsByUuid && climbType === "BOULDER" ? (
          <Map
            map={mapsByUuid[map]}
            mapObjects={routesByMap?.[map] || emptyArray}
            bboxPadding={[10, 10, 30, 10]}
            maxZoom={10}
            idAccessor={(route) => route.uuid}
            zoomToBbox={false}
            selectedIds={selectedUuidsSet}
            onSetSelectedIds={(routeUuids) => {
              const routeUuid = routeUuids[0];
              const route = routes?.find((r) => r.uuid === routeUuid);
              if (route) {
                setRoute(route);
              }
            }}
            brushEnabled={false}
            renderMapObject={(r, transform, isSelected) => {
              const scale = route ? (isSelected ? 1.5 : 0.5) : 1;
              return (
                <g transform={`translate(${r.mapPosition.x}, ${r.mapPosition.y})`} key={r.uuid}>
                  <g transform={`scale(${scale / Math.min(transform.k, 4)})`} opacity={1}>
                    <MapRoutePinSvgRaw
                      route={r}
                      dragToMoveEnabled={false}
                      onClick={() => setRoute(r)}
                    />
                  </g>
                </g>
              );
            }}
          />
        ) : null}
        {map && mapsByUuid && climbType === "SPORT" ? (
          <SportMap
            map={mapsByUuid[map]}
            key={map}
            linesRoutes={linesRoutesByMap?.[map] || emptyArray}
            nonLineRoutes={nonLineRoutesByMap[map] || emptyArray}
            filteredRouteUuids={emptyRouteUuids}
            onClick={(routeUuid: string) => {
              const route = routes?.find((r) => r.uuid === routeUuid);
              if (route) {
                setRoute(route);
              }
            }}
            expandedLineUuid={expandedLineUuid}
            onExpandLine={setExpandedLineUuid}
            selectedRouteUuids={selectedUuidsSet}
            selectedLineUuids={new Set() as Set<string>}
            canChangeSelection
            brushEnabled={false}
            showMapInfo={false}
          />
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          height: 0,
          position: "absolute",
          bottom: 32,
          left: 0,
          right: 4,
        }}
      >
        <MapSwitcher
          map={map}
          onChange={(updatedMap) => {
            setMap(updatedMap);
          }}
          mapsByUuid={selectableMapsByUuid}
        />
      </Box>
    </Stack>
  );
};

export const LineSelectionMapInput = ({ name }: { name: string }) => {
  const { control } = useFormContext();
  const { field: lineField } = useController({
    control,
    name,
  });

  const line = useCurrentVenueLineLookup(lineField.value);

  return (
    <LineSelectionMap line={line ?? null} setLine={(value) => lineField.onChange(value.uuid)} />
  );
};

export const RouteSelectionMapInput = ({
  name,
  climbType,
}: {
  name: string;
  climbType: ClimbType;
}) => {
  const { control } = useFormContext();
  const { field: routeField } = useController({
    control,
    name,
  });

  const route = useCurrentVenueRouteLookup(routeField.value);

  return (
    <RouteSelectionMap
      route={route ?? null}
      climbType={climbType}
      setRoute={(value) => routeField.onChange(value.uuid)}
    />
  );
};

type LineMapEditorProps = {
  mapPosition: { x: number; y: number } | null;
  map: string | null;
  setMapPosition: (mapPosition: { x: number; y: number }) => void;
  setMap: (map: string | null) => void;
  disabled?: boolean;
  filterPredicate?: (line: ApiLine) => boolean;
  line?: ApiLine;
};

export const LineMapEditor = ({
  mapPosition,
  map: mapUuid,
  setMapPosition,
  setMap,
  disabled,
  line,
}: LineMapEditorProps) => {
  const { data: venue } = useCurrentVenueQuery();
  const { data: lines } = useVenueLinesQuery(venue?.uuid);

  const defaultValues = useMemo(() => {
    const defaultMap = venue?.maps.find((m) => m.available);
    if (defaultMap) {
      return {
        map: defaultMap?.uuid,
        mapPosition: { x: defaultMap.width / 2, y: defaultMap.height / 2 },
      };
    }
    return null;
  }, [venue?.maps]);

  mapUuid = (mapUuid || defaultValues?.map) ?? null;
  mapPosition = (mapPosition || defaultValues?.mapPosition) ?? null;

  const { data: mapsByUuid } = useCurrentVenueMapsByUuidQuery();

  const linesByMap = useMemo(() => {
    if (!lines) {
      return emptyObject as {
        ""?: ApiLine[];
      } & Record<string, ApiLine[]>;
    }
    const result: { ""?: ApiLine[] } & Record<string, ApiLine[]> = {};

    lines.forEach((l) => {
      const mapUuid = l.map && l.mapPosition ? l.map : "";
      if (!result[mapUuid]) {
        result[mapUuid] = [];
      }
      result[mapUuid].push(l);
    });

    if (!line?.uuid && mapUuid && mapPosition) {
      // If no line passed, we're creating one, so display a "fake" line
      result[mapUuid] = result[mapUuid] || [];
      result[mapUuid].push({
        uuid: "",
        name: "",
        map: mapUuid,
        mapPosition,
        isActive: true,
        quickdrawCount: 0,
        height: 100,
        autoBelay: false,
        venue: "",
        canLead: true,
        canToprope: true,
      });
    }

    return result;
  }, [lines, line, mapUuid, mapPosition]);

  const [draggingPosition, setDraggingPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    // Reset dragging position when line changes
    setDraggingPosition(null);
  }, [line?.uuid]);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const draggingPositionRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    draggingPositionRef.current = draggingPosition;
  }, [draggingPosition]);

  const onDragStart = () => {
    if (!mapContainerRef.current || disabled) {
      return;
    }
    setDraggingPosition(mapPosition);
    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientX = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
      const clientY = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;

      const pinPos = {
        x: clientX - MOUSE_OFFSET.x,
        y: clientY - MOUSE_OFFSET.y,
      };
      const mapEl = mapContainerRef.current?.children[0] as HTMLDivElement | undefined;
      if (!mapEl) {
        throw new Error("Map container missing");
      }
      const rect = mapEl.getBoundingClientRect();
      const mapScreenCoords = [pinPos.x - rect.x, pinPos.y - rect.y];
      const mapTransform = mapEl.dataset.transform
        ? new Transform(...(JSON.parse(mapEl.dataset.transform) as [number, number, number]))
        : undefined;
      if (!mapTransform) {
        throw new Error("Map transform missing");
      }
      const mapPosition = {
        x: mapTransform.invertX(mapScreenCoords[0]),
        y: mapTransform.invertY(mapScreenCoords[1]),
      };
      setDraggingPosition(mapPosition);
    };
    const onEnd = () => {
      setMapPosition(draggingPositionRef.current!);
      setDraggingPosition(null);
      if (!mapContainerRef.current) {
        return;
      }
      mapContainerRef.current.removeEventListener("mousemove", onMove);
      mapContainerRef.current.removeEventListener("touchmove", onMove);
      mapContainerRef.current.removeEventListener("mouseup", onEnd);
      mapContainerRef.current.removeEventListener("touchend", onEnd);
    };
    mapContainerRef.current.addEventListener("mousemove", onMove);
    mapContainerRef.current.addEventListener("touchmove", onMove);
    mapContainerRef.current.addEventListener("mouseup", onEnd);
    mapContainerRef.current.addEventListener("touchend", onEnd);
  };

  const editingLine = line;

  return (
    <Stack sx={{ backgroundColor: "neutral.900", position: "relative" }}>
      <Box
        sx={{ height: "390px" }}
        className="map-container"
        ref={mapContainerRef}
        onClick={(e) => {
          if (disabled) {
            return;
          }
          // Get the click location relative to the map container
          const mapEl = mapContainerRef.current?.children[0] as HTMLDivElement | undefined;
          if (!mapEl) {
            return;
          }
          const rect = mapEl.getBoundingClientRect();
          const mapScreenCoords = [e.clientX - rect.x, e.clientY - rect.y];
          const mapTransform = mapEl.dataset.transform
            ? new Transform(...(JSON.parse(mapEl.dataset.transform) as [number, number, number]))
            : undefined;
          if (!mapTransform) {
            return;
          }
          const mapPosition = {
            x: mapTransform.invertX(mapScreenCoords[0]),
            y: mapTransform.invertY(mapScreenCoords[1]),
          };
          setDraggingPosition(mapPosition);
          setMapPosition(mapPosition);
        }}
      >
        {mapUuid && mapsByUuid ? (
          <Map
            map={mapsByUuid[mapUuid]}
            mapObjects={linesByMap[mapUuid] || emptyArray}
            bboxPadding={[10, 10, 30, 10]}
            maxZoom={10}
            idAccessor={(line) => line.uuid}
            zoomToBbox={false}
            selectedIds={new Set()}
            onSetSelectedIds={() => null}
            brushEnabled={false}
            renderMapObject={(line, transform, _isSelected) => {
              const scale =
                editingLine?.uuid === line.uuid || (!editingLine && line.uuid === "") ? 1.5 : 0.5;
              const position =
                (line.uuid === editingLine?.uuid || (!editingLine && line.uuid === "")) &&
                draggingPosition
                  ? draggingPosition
                  : line.mapPosition;
              return (
                <g transform={`translate(${position.x}, ${position.y})`} key={line.uuid}>
                  <g transform={`scale(${scale / Math.min(transform.k, 4)})`} opacity={1}>
                    <MapLinePinSvg
                      line={line}
                      dragToMoveEnabled={!disabled}
                      onDragStart={onDragStart}
                      onClick={() => null}
                    />
                  </g>
                </g>
              );
            }}
          />
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          height: 0,
          position: "absolute",
          right: 8,
          bottom: 40,
        }}
      >
        <MapSwitcher map={mapUuid} onChange={(value) => setMap(value)} mapsByUuid={mapsByUuid} />
      </Box>
    </Stack>
  );
};

/**
 * A component for setting the map position of a line.
 */
export const LineMapPositionInput = ({
  prefix,
  line,
  disabled = false,
}: {
  prefix?: string;
  line?: ApiLine;
  disabled?: boolean;
}) => {
  const applyPrefix = (p: string | undefined, s: string) => (p ? `${p}.${s}` : s);

  const { control } = useFormContext();
  const { field: mapPositionField } = useController({
    control,
    name: applyPrefix(prefix, "mapPosition"),
  });
  const { field: mapField } = useController({
    control,
    name: applyPrefix(prefix, "map"),
  });

  const { data: venue } = useCurrentVenueQuery();

  const defaultMapUuid = venue?.maps.find((m) => m.available)?.uuid;

  console.log("MAPPOS", mapPositionField.value);

  return (
    <LineMapEditor
      mapPosition={mapPositionField.value}
      map={mapField.value}
      line={line}
      setMapPosition={(value) => {
        mapPositionField.onChange(value);
        if (!mapField.value) {
          mapField.onChange(defaultMapUuid);
        }
      }}
      setMap={(value) => mapField.onChange(value)}
      disabled={disabled}
    />
  );
};
